import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Users, Check, X, UserMinus, ShieldAlert, Clock } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { api, formatApiError } from "../lib/api";

const TABS = [
  { key: "approved", label: "Members",  icon: Users },
  { key: "pending",  label: "Pending",  icon: Clock },
];

/**
 * Hydrated member list for a club. Public users see approved members only.
 * Admins / owners see a Pending tab where they can Approve or Decline join
 * requests, plus a Kick button on each approved member.
 */
export default function ClubMembersSection({ clubId, ownerId }) {
  const [tab, setTab] = useState("approved");
  const [data, setData] = useState({ members: [], can_manage: false });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: tab });
      const { data } = await api.get(`/clubs/${clubId}/members?${params.toString()}`);
      setData(data);
    } catch (e) {
      // Non-managers will get 403 when fetching pending; silently fall back to approved
      if (e.response?.status === 403 && tab === "pending") {
        setTab("approved");
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load members");
      }
    } finally { setLoading(false); }
  }, [clubId, tab]);

  useEffect(() => { load(); }, [load]);

  const decide = async (memberId, status) => {
    setBusyId(memberId);
    try {
      await api.patch(`/clubs/${clubId}/members/${memberId}`, { status });
      toast.success(status === "approved" ? "Approved" : status === "declined" ? "Declined" : "Removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  const kick = async (m) => {
    if (!window.confirm(`Remove ${m.user?.first_name || "this member"} from the club?`)) return;
    decide(m.id, "removed");
  };

  const visibleTabs = data.can_manage ? TABS : TABS.filter((t) => t.key === "approved");

  return (
    <div data-testid="club-members-section">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="font-heading text-xl font-bold flex items-center gap-2">
          <Users size={18} className="text-[#00E676]" />
          Members
        </h3>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 text-zinc-400">
          {data.members.length}
        </span>
      </div>

      {visibleTabs.length > 1 && (
        <div className="flex gap-2 mb-3">
          {visibleTabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`club-members-tab-${key}`}
              className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 border transition-colors ${
                tab === key
                  ? "bg-[#00E676] text-black border-[#00E676]"
                  : "border-white/10 text-zinc-400 hover:border-white/30"
              }`}
            >
              <Icon size={11} /> {label}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-2">
        {loading && <div className="text-zinc-500 text-sm">Loading…</div>}
        {!loading && data.members.length === 0 && (
          <div className="text-zinc-500 text-sm" data-testid="club-members-empty">
            {tab === "pending" ? "No pending requests." : "No members yet."}
          </div>
        )}
        {data.members.map((m) => (
          <div
            key={m.id}
            data-testid={`club-member-${m.id}`}
            className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5"
          >
            <Avatar className="h-9 w-9 shrink-0">
              <AvatarImage src={m.user?.avatar_url || ""} />
              <AvatarFallback className="bg-[#007AFF] text-white text-xs">
                {(m.user?.first_name?.[0] || "?").toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium truncate">
                  {m.user?.first_name || "Player"} {m.user?.last_name || ""}
                </span>
                {m.membership_type === "admin" && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FFD60A]/10 text-[#FFD60A] uppercase flex items-center gap-1">
                    <ShieldAlert size={9} /> Admin
                  </span>
                )}
                {ownerId === m.user?.id && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#00E676]/10 text-[#00E676] uppercase">
                    Owner
                  </span>
                )}
                {m.status !== "approved" && (
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full uppercase ${
                    m.status === "pending" ? "bg-[#FFD60A]/10 text-[#FFD60A]"
                    : m.status === "declined" ? "bg-[#FF3B30]/10 text-[#FF3B30]"
                    : "bg-white/5 text-zinc-500"
                  }`}>
                    {m.status}
                  </span>
                )}
              </div>
              {m.user?.email && (
                <div className="text-xs text-zinc-500 truncate">{m.user.email}</div>
              )}
            </div>

            {/* Admin actions */}
            {data.can_manage && m.status === "pending" && (
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={() => decide(m.id, "approved")}
                  disabled={busyId === m.id}
                  data-testid={`club-member-approve-${m.id}`}
                  className="px-2 py-1 rounded-lg text-[10px] border border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10 flex items-center gap-1 disabled:opacity-50"
                >
                  <Check size={10} /> Approve
                </button>
                <button
                  onClick={() => decide(m.id, "declined")}
                  disabled={busyId === m.id}
                  data-testid={`club-member-decline-${m.id}`}
                  className="px-2 py-1 rounded-lg text-[10px] border border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10 flex items-center gap-1 disabled:opacity-50"
                >
                  <X size={10} /> Decline
                </button>
              </div>
            )}
            {data.can_manage && m.status === "approved" && ownerId !== m.user?.id && (
              <button
                onClick={() => kick(m)}
                disabled={busyId === m.id}
                data-testid={`club-member-kick-${m.id}`}
                className="shrink-0 px-2 py-1 rounded-lg text-[10px] border border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10 flex items-center gap-1 disabled:opacity-50"
                title="Remove from club"
              >
                <UserMinus size={10} /> Kick
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
